import React, { useState } from 'react';

import Modal from 'components/shared/Modal';
import Button from 'components/shared/Button';

import ProjectActionAlert from './ProjectActionAlert';
import ProjectActionTile from './ProjectActionTile';

import type { Project } from 'domains/projects';

type LoadProjectModalProps = {
  projects: Project[];
  onShowProjects: () => void;
  onRestore: (project: Project) => void;
  onDelete: (project: Project) => void;
  onClose: () => void;
};

export type LoadProjectAlertType = 'restore' | 'delete' | null;

function LoadProjectModal(props: LoadProjectModalProps) {
  const { projects, onShowProjects, onRestore, onDelete, onClose } = props;
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<LoadProjectAlertType>(null);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [lastFocusedElementId, setLastFocusedElementId] = useState('');

  const mostRecentProjects = [...projects]
    .filter((project) => !project.last_ordered_at)
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .splice(0, 8);

  function handleOpenAlert(whichAlert: LoadProjectAlertType, project: Project) {
    setAlertType(whichAlert);
    setActiveProject(project);
    updateLastFocusedEl(whichAlert);
    setShowAlert(true);
  }

  function updateLastFocusedEl(whichAlert: LoadProjectAlertType) {
    switch (whichAlert) {
      case 'restore':
        setLastFocusedElementId('restore-project-button');
        break;
      case 'delete':
        setLastFocusedElementId('remove-project-button');
        break;
      default:
        setLastFocusedElementId('');
        break;
    }
  }

  function handleContinue() {
    setShowAlert(false);
    if (!activeProject) return;

    if (alertType === 'restore') {
      onRestore(activeProject);
      onClose();
    }

    if (alertType === 'delete') {
      onDelete(activeProject);
    }
  }

  function handleCancel() {
    setAlertType(null);
    setActiveProject(null);
    setShowAlert(false);
  }

  function handleShowProjectsList() {
    onShowProjects();
    onClose();
  }

  const projectTiles = mostRecentProjects.map((project) => (
    <li key={project.id} aria-label={`project named ${project.name}`}>
      <ProjectActionTile
        project={project}
        appearance="light"
        onDelete={(project) => handleOpenAlert('delete', project)}
        onRestore={(project) => handleOpenAlert('restore', project)}
      />
    </li>
  ));

  return (
    <Modal
      title="Most Recent Projects"
      labelledBy="load-project-modal"
      onClose={onClose}
      lastFocusedElementId="projects-menu-trigger"
      primaryFooterGroup={
        projects.length > 0 ? (
          <Button type="link" onClick={handleShowProjectsList}>
            View all saved projects
          </Button>
        ) : null
      }
    >
      <div className="load-project-form">
        {showAlert && alertType && (
          <ProjectActionAlert
            alertType={alertType}
            lastFocusedElementId={lastFocusedElementId}
            onCancel={handleCancel}
            onContinue={handleContinue}
            onClose={handleCancel}
          />
        )}
        {projectTiles.length > 0 ? (
          <ul className="modal-list-items" aria-label="projects list">
            {projectTiles}
          </ul>
        ) : (
          'You have 0 saved projects'
        )}
      </div>
    </Modal>
  );
}

export default LoadProjectModal;
